/**
 * React Query Client Configuration
 * Optimized cache strategies for different data types
 * FLOW[24397]: MS209 - Integrated standardized error handling
 * @module lib/queryClient
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { parseApiError, handleApiError, type ApiErrorResponse } from '@/hooks/api/useQueryDefaults';
import { shouldSuppressGlobalError } from './queryErrorPolicy';

// ============================================================================
// CACHE TIME CONSTANTS
// ============================================================================

export const CacheTimes = {
  /** Static data that rarely changes (roles, permissions, settings) */
  STATIC: 30 * 60 * 1000, // 30 minutes

  /** Reference data (catalogs, categories, templates) */
  REFERENCE: 10 * 60 * 1000, // 10 minutes

  /** User session data */
  SESSION: 5 * 60 * 1000, // 5 minutes

  /** Dynamic business data (orders, clients, production) */
  DYNAMIC: 60 * 1000, // 1 minute

  /** Real-time data (notifications, stats) */
  REALTIME: 30 * 1000, // 30 seconds

  /** Analytics data (can be cached longer) */
  ANALYTICS: 5 * 60 * 1000, // 5 minutes
} as const;

export const StaleTime = {
  /** Data that should always be fresh */
  NONE: 0,

  /** Quick stale for frequently updated data */
  SHORT: 10 * 1000, // 10 seconds

  /** Default stale time */
  DEFAULT: 30 * 1000, // 30 seconds

  /** Medium stale for semi-static data */
  MEDIUM: 2 * 60 * 1000, // 2 minutes

  /** Long stale for static data */
  LONG: 10 * 60 * 1000, // 10 minutes

  /** Infinite - data never becomes stale */
  INFINITE: Infinity,
} as const;

// ============================================================================
// ERROR HANDLER - FLOW[24398]: MS209 - Standardized error handling
// ============================================================================

/**
 * FLOW[24399]: Global query error handler using standardized parsing
 * Handles all query errors with proper Russian messages
 */
function handleQueryError(error: unknown): void {
  // Check if it's an Axios error
  const axiosError = error as AxiosError<ApiErrorResponse>;

  if (axiosError.response) {
    const parsed = parseApiError(axiosError);

    const status = axiosError.response.status;
    const requestUrl = axiosError.config?.url;

    // Don't show error for 401 (handled by auth interceptor)
    if (parsed.isAuthError && status === 401) {
      return;
    }

    // FLOW[45539]: PRW-W4 (PRW-02) — 403 на фоновом чтении не пугает оператора
    // красным «Доступ запрещён»: разделы без прав скрыты интерфейсом, отказ RBAC
    // на query — ожидаемый ответ. Мутации (явное действие) показывают 403 как раньше.
    if (status === 403) {
      console.warn('[QueryClient] 403 on background query (toast suppressed):', requestUrl);
      return;
    }

    if (shouldSuppressGlobalError(requestUrl, status)) {
      return;
    }

    // Use detailed handler for proper UI feedback
    handleApiError(axiosError, { showMessage: true, redirectOnAuth: false });
  } else if (axiosError.request) {
    // Network error - request made but no response
    console.error('[QueryClient] Network error:', error);
  } else {
    // Something else went wrong
    console.error('[QueryClient] Query error:', error);
  }
}

/**
 * FLOW[24400]: Global mutation error handler using standardized parsing
 * Handles all mutation errors with proper Russian messages
 */
function handleMutationError(error: unknown): void {
  const axiosError = error as AxiosError<ApiErrorResponse>;

  if (axiosError.response) {
    // Use detailed handler with notification for mutations (more visible)
    handleApiError(axiosError, {
      showMessage: false,
      showNotification: true,
      redirectOnAuth: true
    });
  } else {
    console.error('[QueryClient] Mutation error:', error);
  }
}

// ============================================================================
// QUERY CLIENT FACTORY
// ============================================================================

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleQueryError,
    }),
    mutationCache: new MutationCache({
      onError: handleMutationError,
    }),
    defaultOptions: {
      queries: {
        // Default stale time - data is fresh for 30 seconds
        staleTime: StaleTime.DEFAULT,

        // Garbage collection time - keep in cache for 10 minutes after becoming inactive
        gcTime: CacheTimes.REFERENCE,

        // Only retry once on failure
        retry: 1,

        // Retry delay with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Don't refetch on window focus by default (reduces unnecessary requests)
        refetchOnWindowFocus: false,

        // Don't refetch on reconnect by default
        refetchOnReconnect: 'always',

        // Don't refetch on mount if data is fresh
        refetchOnMount: true,

        // Network mode - fetch even if offline (will use cache)
        networkMode: 'offlineFirst',
      },
      mutations: {
        // Retry mutations once
        retry: 1,

        // Network mode for mutations
        networkMode: 'online',
      },
    },
  });
}

// ============================================================================
// QUERY OPTIONS PRESETS
// ============================================================================

/**
 * Query options for static/reference data (roles, permissions, categories)
 * Long cache, rarely refetched
 */
export const staticQueryOptions = {
  staleTime: StaleTime.LONG,
  gcTime: CacheTimes.STATIC,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
} as const;

/**
 * Query options for user session data
 */
export const sessionQueryOptions = {
  staleTime: StaleTime.MEDIUM,
  gcTime: CacheTimes.SESSION,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
} as const;

/**
 * Query options for dynamic business data (orders, clients)
 */
export const dynamicQueryOptions = {
  staleTime: StaleTime.DEFAULT,
  gcTime: CacheTimes.DYNAMIC,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
} as const;

/**
 * Query options for real-time data (notifications, live stats)
 */
export const realtimeQueryOptions = {
  staleTime: StaleTime.SHORT,
  gcTime: CacheTimes.REALTIME,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  refetchInterval: 30000, // Poll every 30 seconds
} as const;

/**
 * Query options for analytics data
 */
export const analyticsQueryOptions = {
  staleTime: StaleTime.MEDIUM,
  gcTime: CacheTimes.ANALYTICS,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
} as const;

/**
 * Query options for search/filter results (short cache, frequently changes)
 */
export const searchQueryOptions = {
  staleTime: StaleTime.NONE,
  gcTime: 60 * 1000, // 1 minute
  refetchOnMount: true,
} as const;

// ============================================================================
// PREFETCH HELPERS
// ============================================================================

/**
 * Prefetch critical data on app initialization
 */
export async function prefetchCriticalData(client: QueryClient): Promise<void> {
  // This can be extended to prefetch critical data like user settings, roles, etc.
  // Example:
  // await client.prefetchQuery({
  //   queryKey: ['settings'],
  //   queryFn: () => settingsApi.getSettings(),
  //   ...staticQueryOptions,
  // });
  void client; // Mark as used until implementation
}

/**
 * Invalidate all queries in a namespace
 */
export function invalidateNamespace(client: QueryClient, namespace: string): void {
  client.invalidateQueries({ queryKey: [namespace] });
}

/**
 * Clear cache for a specific namespace
 */
export function clearNamespace(client: QueryClient, namespace: string): void {
  client.removeQueries({ queryKey: [namespace] });
}

// Singleton instance - created once and exported
let _queryClient: QueryClient | null = null;

export function getQueryClient(): QueryClient {
  if (!_queryClient) {
    _queryClient = createQueryClient();
  }
  return _queryClient;
}

// Export singleton instance
export const queryClient = getQueryClient();
